/** Constantes de domaine transverses. */

// --- Interventions (§6.1) ---------------------------------------------------
export const INTERVENTION_TYPES = [
  'installation',
  'visite_contrat',
  'depannage',
  'controle_etancheite',
] as const;
export type InterventionType = (typeof INTERVENTION_TYPES)[number];

export const INTERVENTION_PRIORITIES = ['normale', 'urgente'] as const;
export type InterventionPriority = (typeof INTERVENTION_PRIORITIES)[number];

// --- Demandes client (QR et portail, §5) --------------------------------------
/** Symptômes proposés au client ; « autre » laisse la description faire le reste. */
export const SERVICE_REQUEST_SYMPTOMS = [
  'temperature_haute',
  'temperature_basse',
  'bruit_anormal',
  'fuite_eau',
  'givre_important',
  'arret_complet',
  'alarme_affichee',
  'autre',
] as const;
export type ServiceRequestSymptom = (typeof SERVICE_REQUEST_SYMPTOMS)[number];

/** Urgence telle que le client la ressent : indicative, l'entreprise qualifie. */
export const PERCEIVED_URGENCIES = ['faible', 'normale', 'haute', 'critique'] as const;
export type PerceivedUrgency = (typeof PERCEIVED_URGENCIES)[number];

// --- Événements non-intervention (§6.4) --------------------------------------
/**
 * Types qu'on peut poser. Le module Équipe › Astreintes est la seule source
 * d'astreinte (décision du propriétaire du 19/10/2026) : il n'y a plus de
 * type « astreinte » à la création.
 */
export const SCHEDULE_EVENT_CREATE_KINDS = [
  'indisponibilite',
  'conge',
  'rendez_vous_interne',
  'formation',
  // École d'un alternant (15/10/2026) : des jours choisis semaine par semaine ;
  // bloque le planning comme une formation. Posée par l'alternant lui-même, elle
  // part en validation comme un congé (16/10/2026).
  'ecole',
  'recuperation_pieces',
  'trajet',
  'tache_administrative',
  'blocage_provisoire',
  'rappel_personnel',
  // Texte libre posé à une heure (15/10/2026) : ne bloque pas le créneau,
  // ne crée aucun conflit et ne demande aucune validation.
  'note',
] as const;

/**
 * Types lisibles : les précédents, plus `astreinte` pour les créneaux déjà
 * posés, qui ne bloquent que l'agenda et ne préviennent personne.
 */
export const SCHEDULE_EVENT_KINDS = [...SCHEDULE_EVENT_CREATE_KINDS, 'astreinte'] as const;
export type ScheduleEventKind = (typeof SCHEDULE_EVENT_KINDS)[number];

/**
 * La personne n'est pas disponible pour l'entreprise : « Absent » dans
 * l'équipe du jour, retiré de la capacité des analyses. L'école d'un alternant
 * en fait partie (15/10/2026) ; une formation reste du temps bloqué.
 */
export const AWAY_EVENT_KINDS: readonly ScheduleEventKind[] = ['conge', 'indisponibilite', 'ecole'];

/**
 * Kinds soumis à validation patron/dispatcher (Q09). L'école d'un alternant
 * (décision du propriétaire du 16 octobre 2026) en fait partie quand il la pose
 * lui-même : elle part en validation comme un congé. Posée par qui organise son
 * travail, elle est acceptée d'emblée (voir `needsAbsenceApproval`).
 */
export const APPROVAL_REQUIRED_EVENT_KINDS: readonly ScheduleEventKind[] = [
  'conge',
  'indisponibilite',
  'ecole',
];

/** Kinds validés seulement quand la personne les pose pour elle-même. */
export const SELF_APPROVAL_EVENT_KINDS: readonly ScheduleEventKind[] = ['ecole'];

/**
 * Le créneau part-il en validation ? Jamais pour qui détient le droit de
 * valider. Congé et indisponibilité : toujours sinon. École : seulement posée
 * par la personne pour elle-même (le bureau qui la pose pour un alternant la
 * fait accepter d'emblée).
 */
export function needsAbsenceApproval(
  kind: string,
  { forSelf, approver }: { forSelf: boolean; approver: boolean },
): boolean {
  if (approver || !APPROVAL_REQUIRED_EVENT_KINDS.includes(kind as ScheduleEventKind)) return false;
  return SELF_APPROVAL_EVENT_KINDS.includes(kind as ScheduleEventKind) ? forSelf : true;
}

// --- Temps (Q17) ---------------------------------------------------------------
export const TIME_CATEGORIES = ['trajet', 'travail', 'pause', 'administratif'] as const;
export type TimeCategory = (typeof TIME_CATEGORIES)[number];

// --- Stock (§8) -------------------------------------------------------------------
export const ARTICLE_CATEGORIES = ['piece', 'machine', 'gaz_fluide', 'consommable'] as const;
export type ArticleCategory = (typeof ARTICLE_CATEGORIES)[number];

/** Comment un article se compte : à l'unité, au poids, au volume ou à la longueur (tube, câble). */
export const ARTICLE_UNITS = ['unite', 'kg', 'litre', 'metre'] as const;
export type ArticleUnit = (typeof ARTICLE_UNITS)[number];

/**
 * Une quantité saisie (millièmes) convient-elle à l'unité de l'article ? Un
 * article « à l'unité » se compte en entiers ; kilos, litres et mètres
 * acceptent les décimales. Vérifié à la saisie (écran et serveur), jamais au
 * rejeu d'un mouvement déjà enregistré ni à la correction d'un stock existant.
 */
export function quantityFitsUnit(unit: string | null | undefined, qtyMilli: number): boolean {
  return unit !== 'unite' || qtyMilli % 1000 === 0;
}

export const STOCK_MOVEMENT_TYPES = [
  'entree',
  'sortie',
  'ajustement',
  'transfert',
  'retour',
  'compensation', // corrections d’inventaire (Q180)
] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];

export const STOCK_LOCATION_KINDS = ['depot', 'vehicule', 'quarantaine'] as const;
export type StockLocationKind = (typeof STOCK_LOCATION_KINDS)[number];

/** Classement d'une pièce retournée après contrôle (Q195). */
export const RETURN_DISPOSITIONS = [
  'vendable',
  'defectueuse',
  'a_reparer',
  'retour_fournisseur',
] as const;
export type ReturnDisposition = (typeof RETURN_DISPOSITIONS)[number];

// --- Assistance MyEquip (11/10/2026) -----------------------------------------------------
/** Sujets proposés quand une entreprise écrit à l'assistance. */
export const SUPPORT_REQUEST_TOPICS = ['question', 'probleme', 'idee', 'compte', 'facturation'] as const;
export type SupportRequestTopic = (typeof SUPPORT_REQUEST_TOPICS)[number];
/**
 * « attente_client » (12/10/2026) : l'assistance a répondu, c'est au membre de
 * reprendre la main. Le statut avance avec le fil de messages.
 */
export const SUPPORT_REQUEST_STATUSES = ['nouvelle', 'prise_en_charge', 'attente_client', 'resolue'] as const;
export type SupportRequestStatus = (typeof SUPPORT_REQUEST_STATUSES)[number];
/** Longueur d'un message du fil, et nombre de messages d'une conversation. */
export const SUPPORT_MESSAGE_MAX = 4000;
export const SUPPORT_THREAD_MAX = 200;

// --- Avis sur MyEquip (24/10/2026) ---------------------------------------------------------
/** Un avis en un geste, sans conversation : il devient une issue du dépôt GitHub de MyEquip. */
export const PRODUCT_FEEDBACK_KINDS = ['bug', 'idee', 'autre'] as const;
export type ProductFeedbackKind = (typeof PRODUCT_FEEDBACK_KINDS)[number];
export const PRODUCT_FEEDBACK_MIN = 10;
export const PRODUCT_FEEDBACK_MAX = 4000;

// --- Interventions et demandes client -------------------------------------------------
/**
 * Notes d'une intervention : une seule borne pour la création, la modification,
 * la synchronisation hors ligne et la conversion d'une demande (« Demande
 * DEM-… : » suivi d'une description de 4 000 caractères au plus y tient).
 */
export const INTERVENTION_NOTES_MAX = 5000;
/** Réponse du demandeur depuis la page de suivi, quand l'entreprise lui demande une précision. */
export const REQUEST_REPLY_MAX = 2000;
/** Photos jointes à une demande ou à une réponse depuis une page publique. */
export const PUBLIC_REQUEST_PHOTOS_MAX = 3;

// --- Parc machines (§4.3) ---------------------------------------------------------------
/** Catégories proposées à la saisie. Le serveur accepte aussi les valeurs déjà enregistrées. */
export const EQUIPMENT_CATEGORIES = [
  'chambre_froide',
  'vitrine_refrigeree',
  'meuble_frigorifique',
  'groupe_frigorifique',
  'centrale_froid',
  'climatisation',
  'pompe_a_chaleur',
  'autre',
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

// --- Devis (§11) ----------------------------------------------------------------------
/**
 * Types de ligne d'un devis, de ses barèmes et de leurs factures, dans l'ordre du
 * sélecteur (la pièce d'abord, la main-d'œuvre ensuite : demande relayée le 12/10/2026).
 */
export const QUOTE_LINE_KINDS = ['piece', 'main_oeuvre', 'deplacement', 'consommable', 'forfait', 'frais'] as const;
export type QuoteLineKind = (typeof QUOTE_LINE_KINDS)[number];
/**
 * Types d'une ligne de facture saisie : ceux du devis, plus le fluide (relevé d'intervention).
 * L'ancien type « Remise » n'est plus accepté (constat API3-09 : saisi en positif, il
 * augmentait la facture) ; il reste lisible sur les documents anciens (`LEGACY_LINE_KINDS`).
 * Une remise se pose en pourcentage sur la ligne (`discountBp`).
 */
export const INVOICE_LINE_KINDS = [...QUOTE_LINE_KINDS, 'fluide'] as const;
export type InvoiceLineKind = (typeof INVOICE_LINE_KINDS)[number];
/** Types gardés en base pour l'historique, refusés à la saisie. */
export const LEGACY_LINE_KINDS = ['remise'] as const;

// --- Factures (§12) ----------------------------------------------------------------------
export const INVOICE_KINDS = ['facture', 'avoir', 'acompte', 'situation'] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

/**
 * Moyens d'un paiement enregistré à la main (Q39). `banque` n'en fait pas partie : il
 * n'arrive que par le rapprochement comptable (`source = 'comptabilite'`).
 */
export const PAYMENT_METHODS = ['virement', 'cheque', 'especes', 'carte'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
/** Référence d'un paiement (n° de chèque, virement) : longueur maximale, écran et serveur. */
export const PAYMENT_REFERENCE_MAX = 200;

export const DOCUMENT_SEQUENCE_KINDS = ['facture', 'devis', 'avoir'] as const;
export type DocumentSequenceKind = (typeof DOCUMENT_SEQUENCE_KINDS)[number];

// --- Fluides (§9) : opérations du registre dans `fluid-register.ts` -----------------------------
export const CYLINDER_STATUSES = [
  'pleine',
  'en_service',
  'a_recuperer',
  'pour_destruction',
  'retournee',
] as const;
export type CylinderStatus = (typeof CYLINDER_STATUSES)[number];

// --- Clients (§4) -------------------------------------------------------------------------------
export const CUSTOMER_KINDS = ['professionnel', 'particulier'] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];

// --- Historique machine : attribution (Q14, §5.4) -------------------------------------------------
export const HISTORY_SOURCES = ['technicien', 'bureau', 'client', 'systeme'] as const;
export type HistorySource = (typeof HISTORY_SOURCES)[number];

// --- Notifications (§17) ----------------------------------------------------------------------------
export const NOTIFICATION_EVENTS = [
  'tache_recue',
  'tache_faite',
  'tache_planifiee',
  // Objectifs (15/10/2026) : rappel doux, demande d'aide, heure passée.
  'objectif_rappel',
  'objectif_bloque',
  'objectif_non_atteint',
  // Oubli de fin d'intervention (15/10/2026) : rappel au technicien, puis avis au bureau.
  'oubli_terminer',
  'oubli_signale',
  // Réunion posée dans le planning d'autres personnes (15/10/2026).
  'reunion_planifiee',
  // Astreinte planifiée pour quelqu'un d'autre (19/10/2026) : la personne l'apprend.
  'astreinte_planifiee',
  'affectation_intervention',
  'changement_intervention',
  'technicien_en_route',
  'demande_information',
  'rapport_disponible',
  // Export demandé (CSV, Excel, PDF) terminé : le fichier est prêt à télécharger.
  'export_pret',
  'devis_disponible',
  'facture_disponible',
  'devis_a_relancer',
  'devis_expire',
  'facture_echue',
  'stock_bas',
  'contrat_echeance',
  'machine_a_surveiller',
  // Brouillon de facture préparé pour une période d'un contrat (décision 10, 19/10/2026).
  'facture_contrat_a_verifier',
  'controle_etancheite_echeance',
  // Suites de fuite et détecteurs (CMP-4) ; `cerfa_disponible` est un avis au détenteur (client).
  'fuite_constatee',
  'controle_etancheite_absent',
  'etalonnage_detecteur_echeance',
  'attestation_echeance',
  'cerfa_disponible',
  'echec_synchronisation',
  'echec_integration',
  // Fichier rejeté par l'antivirus ou dont l'analyse est impossible : à renvoyer.
  'fichier_bloque',
  'urgence_critique',
  'absence_a_valider',
  'absence_decidee',
  'conflit_planning',
  'conflit_synchronisation',
  'reservation_expiree',
  'anomalie_stock',
  'signature_impossible',
  // Clôture faite hors ligne puis refusée par le serveur (20/10/2026) : le bureau la reprend.
  'cloture_refusee',
  'demande_client_recue',
  'assistance_plateforme',
  'visite_replanifiee',
  'essai_bientot_termine',
  'essai_termine',
  'sla_alerte',
  'note_client_recue',
  'rappel_mise_a_jour_stock',
  'approbation_requise',
  'suivi_a_planifier',
  'appareil_compromis',
  'accuse_fournisseur',
  // Inventaire envoyé avec des écarts au-delà des seuils (décision 15, 20/10/2026).
  'inventaire_a_approuver',
  // Demande d'achat refusée ou commande annulée (17/10/2026) : son demandeur, avec le motif.
  'commande_refusee',
  'fiche_machine_transferee',
  // Documents de l'entreprise (15/10/2026) : règlement intérieur ou consigne à lire.
  'document_a_lire',
  // Messages (25/10/2026) : message reçu dans une conversation (push seulement, le
  // non-lu est porté par la conversation) ; annonce publiée pour la personne.
  'message_recu',
  'annonce_publiee',
  // Résumé du soir (facultatif) : un seul push qui regroupe les avis non lus du jour.
  'resume_quotidien',
] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const NOTIFICATION_CHANNELS = ['email', 'sms', 'push', 'interne'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/**
 * Canaux qu'un membre règle pour ses alertes (décision 1 du 19/10/2026) : dans
 * l'application et en notification push sur ses appareils. L'e-mail et le SMS
 * servent aux clients et, pour le SMS, aux seules urgences : jamais proposés
 * dans « Mes notifications », où ils ne partaient pas.
 */
export const MEMBER_NOTIFICATION_CHANNELS = ['push', 'interne'] as const satisfies readonly NotificationChannel[];
export type MemberNotificationChannel = (typeof MEMBER_NOTIFICATION_CHANNELS)[number];

/** Alertes critiques jamais regroupées/masquées (§17). */
export const CRITICAL_EVENTS: readonly NotificationEvent[] = ['urgence_critique'];

/**
 * Alertes en push seulement, sans ligne dans le centre de notifications : un message
 * reçu (messagerie, 25/10/2026), dont le non-lu est porté par la conversation.
 */
export const PUSH_ONLY_EVENTS: readonly NotificationEvent[] = ['message_recu'];

/** Canaux qu'un membre règle pour cette alerte : le push seul pour une alerte sans avis au centre. */
export function memberChannelsFor(event: string): readonly MemberNotificationChannel[] {
  return (PUSH_ONLY_EVENTS as readonly string[]).includes(event) ? ['push'] : MEMBER_NOTIFICATION_CHANNELS;
}

/**
 * Une alerte peut être regroupée dans le résumé du soir si elle n'est pas
 * critique, n'est pas le résumé lui-même (un résumé regroupé dans le résumé
 * ne partirait jamais) et laisse un avis au centre : le résumé ne compte que
 * ceux-là, un push seul (message reçu) retenu pour lui ne partirait jamais.
 */
export function digestAllowed(event: string): boolean {
  return !(CRITICAL_EVENTS as readonly string[]).includes(event) && event !== 'resume_quotidien'
    && !(PUSH_ONLY_EVENTS as readonly string[]).includes(event);
}

/**
 * Libellés FR des événements de notification. Sert AUSSI côté serveur : le titre
 * stocké d'une notification interne ne doit jamais être une clé brute comme
 * `approbation_requise` (défaut relevé). Tout événement non listé retombe sur un
 * libellé humanisé — jamais la clé snake_case telle quelle.
 */
const NOTIFICATION_EVENT_LABELS: Record<string, string> = {
  tache_recue: 'Nouvelle tâche',
  tache_faite: 'Tâche terminée',
  tache_planifiee: 'Tâche planifiée',
  objectif_rappel: 'Rappel d’objectif',
  objectif_bloque: 'Objectif bloqué',
  objectif_non_atteint: 'Objectif non atteint',
  oubli_terminer: 'Intervention à terminer',
  oubli_signale: 'Intervention restée ouverte',
  reunion_planifiee: 'Réunion planifiée',
  astreinte_planifiee: 'Astreinte planifiée',
  affectation_intervention: 'Intervention affectée',
  changement_intervention: 'Intervention modifiée',
  technicien_en_route: 'Technicien en route',
  prochain_passage: 'Prochaine intervention planifiée',
  suivi_a_planifier: 'Intervention de retour à affecter',
  demande_information: 'Information demandée',
  rapport_disponible: 'Rapport disponible',
  export_pret: 'Export prêt',
  devis_disponible: 'Devis disponible',
  facture_disponible: 'Facture disponible',
  devis_a_relancer: 'Devis à relancer',
  devis_expire: 'Devis expiré',
  facture_echue: 'Facture échue',
  stock_bas: 'Stock bas',
  contrat_echeance: 'Contrat à échéance',
  machine_a_surveiller: 'Machine à surveiller',
  facture_contrat_a_verifier: 'Facture de contrat à vérifier',
  controle_etancheite_echeance: 'Contrôle d’étanchéité à échéance',
  fuite_constatee: 'Fuite constatée',
  controle_etancheite_absent: 'Contrôle d’étanchéité non enregistré',
  etalonnage_detecteur_echeance: 'Étalonnage de détecteur à renouveler',
  attestation_echeance: 'Attestation fluides à renouveler',
  cerfa_disponible: 'Fiche d’intervention fluides frigorigènes',
  echec_synchronisation: 'Échec de synchronisation',
  echec_integration: 'Échec d’intégration',
  fichier_bloque: 'Fichier bloqué par l’antivirus',
  urgence_critique: 'Urgence critique',
  absence_a_valider: 'Absence à valider',
  absence_decidee: 'Décision sur votre absence',
  conflit_planning: 'Conflit de planning',
  conflit_synchronisation: 'Conflit de synchronisation',
  reservation_expiree: 'Réservation expirée',
  anomalie_stock: 'Anomalie de stock',
  rappel_mise_a_jour_stock: 'Pensez à mettre à jour votre stock',
  appareil_compromis: 'Appareil signalé compromis',
  accuse_fournisseur: 'Accusé de réception fournisseur',
  inventaire_a_approuver: 'Écarts d’inventaire à approuver',
  commande_refusee: 'Commande refusée ou annulée',
  fiche_machine_transferee: 'Nouveau client : fiche machine transférée',
  document_a_lire: 'Document de l’entreprise à lire',
  message_recu: 'Nouveau message',
  annonce_publiee: 'Nouvelle annonce',
  resume_quotidien: 'Résumé du soir',
  signature_impossible: 'Signature impossible',
  cloture_refusee: 'Clôture hors ligne refusée',
  demande_client_recue: 'Nouvelle demande client',
  assistance_plateforme: 'Assistance MyEquip',
  visite_replanifiee: 'Visite d’entretien replanifiée',
  sla_alerte: 'Alerte SLA',
  note_client_recue: 'Avis client reçu',
  approbation_requise: 'Approbation requise',
  essai_bientot_termine: 'Votre essai se termine bientôt',
  essai_termine: 'Essai terminé — accès en lecture seule',
};

const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'E-mail',
  sms: 'SMS',
  push: 'Notification push',
  interne: 'Dans l’application',
};

/** « affectation_intervention » → « Intervention affectée » (jamais la clé brute). */
export function notificationEventLabel(event: string): string {
  return NOTIFICATION_EVENT_LABELS[event] ?? humanizeKey(event);
}

/** « push » → « Notification push ». */
export function notificationChannelLabel(channel: string): string {
  return NOTIFICATION_CHANNEL_LABELS[channel as NotificationChannel] ?? humanizeKey(channel);
}

/** Repli sûr : « snake_case » → « Snake case » — jamais de clé technique affichée. */
export function humanizeKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// --- Types de temps par défaut des politiques d'intervention -------------------------
export const SIGNATURE_POLICIES = ['obligatoire', 'facultative'] as const;
export type SignaturePolicy = (typeof SIGNATURE_POLICIES)[number];

export const SIGNATURE_EXCEPTION_REASONS = [
  'client_absent',
  'client_indisponible',
  'client_refuse',
] as const;
export type SignatureExceptionReason = (typeof SIGNATURE_EXCEPTION_REASONS)[number];

// --- Import (Q91–Q93) ------------------------------------------------------------------------------------
/**
 * Entités que l'import sait réellement écrire, dans l'ordre d'une reprise :
 * les clients, puis leurs sites et contacts, puis leurs machines (qui créent
 * leur site au besoin), puis fournisseurs et articles. Contrats, interventions,
 * documents et soldes ne s'importent pas encore : le serveur les refuse plutôt
 * que d'annoncer un import vide.
 */
export const IMPORT_ENTITIES = ['clients', 'sites', 'contacts', 'equipements', 'fournisseurs', 'articles'] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];
